import * as Sentry from '@sentry/react';
import type { ConsoleConfig } from './config.ts';

/* Error reporting to Sentry (project budovan-admin). The DSN and the env name come from /config.json, so
 * one build serves every env; without a DSN (local development) nothing is sent. Reports carry ids,
 * roles and the route pattern, never names, e-mail addresses or form contents. */

/** What failed, as the `error_type` tag: one Sentry search per kind of failure. */
export const ERROR_TYPE = {
  /** A page threw while rendering and the router showed its error screen. */
  routeRender: 'route_render',
} as const;
type ErrorType = (typeof ERROR_TYPE)[keyof typeof ERROR_TYPE];

const TAG = { errorType: 'error_type', tenant: 'tenant', role: 'role', operator: 'operator' } as const;
type Tag = Exclude<keyof typeof TAG, 'errorType'>;

let enabled = false;

/** Called once with the deploy-time settings; `router` names each page view by its route pattern. */
export function initErrorReporting(config: ConsoleConfig, router: unknown): void {
  if (enabled || !config.sentryDsn) return;
  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment,
    tracesSampleRate: 0.2,
    // Profiles every traced page load and navigation of a sampled session; the page must be served with
    // `Document-Policy: js-profiling` (infra/demo/web/default.conf.template) or the browser refuses.
    profileSessionSampleRate: 0.2,
    profileLifecycle: 'trace',
    tracePropagationTargets: [config.apiUrl],
    // No request bodies (sign-in passwords), cookies, query strings or IP addresses; the user is set by id.
    dataCollection: { userInfo: false, cookies: false, httpBodies: [], urlQueryParams: false },
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.browserProfilingIntegration(),
    ],
  });
  enabled = true;
}

/** The signed-in person by id only. */
export function setSentryUser(user: { id: string }): void {
  if (enabled) Sentry.setUser(user);
}

/** Context every later report carries. */
export function setSentryTags(tags: Partial<Record<Tag, string>>): void {
  if (!enabled) return;
  for (const [key, value] of Object.entries(tags)) Sentry.setTag(TAG[key as Tag], value);
}

/** An error a boundary caught and showed: the console kept running, so the SDK would not see it. */
export function reportError(error: unknown, type: ErrorType): void {
  if (enabled) Sentry.captureException(error, { tags: { [TAG.errorType]: type } });
}
